import React from 'react';
import { Info, DollarSign, Calculator, PiggyBank, Briefcase, AlertTriangle, TrendingUp, Target, BookOpen } from 'lucide-react';

/**
 * Компонент с информацией о финансовой независимости и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о финансовой независимости: концепция, стратегии, советы
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Target className="w-5 h-5 text-green-500" />
          Что такое финансовая независимость?
        </h3>
        <p>
          <strong>Финансовая независимость</strong> — это состояние, при котором человек может жить на пассивный доход от накопленных активов, не нуждаясь в активном заработке. Это не обязательно полный отказ от работы, а скорее свобода выбора: работать или нет, чем заниматься, сколько времени уделять карьере и другим сферам жизни.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Ключевые принципы и формулы
        </h3>
        <ul>
          <li><strong>Правило 4%:</strong> Вы можете безопасно тратить 4% от накопленного капитала ежегодно, с высокой вероятностью того, что капитал не будет исчерпан в течение 30+ лет.</li>
          <li><strong>Формула целевого капитала:</strong> Годовые расходы × 25 = Необходимая сумма накоплений.</li>
          <li><strong>Пример:</strong> Если вам нужно 100 000 ₽ в месяц (1.2 млн ₽ в год), то необходимо накопить примерно 30 млн ₽.</li>
          <li><strong>Срок достижения цели</strong> зависит от нормы сбережений, доходности инвестиций и уже накопленного капитала.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-indigo-500" />
          Этапы финансовой независимости в России
        </h3>
        <ol>
          <li><strong>Финансовая стабильность:</strong> Отсутствие долгов, наличие финансовой подушки на 3-6 месяцев расходов.</li>
          <li><strong>Финансовая безопасность:</strong> Пассивный доход покрывает базовые потребности (питание, коммунальные платежи, минимальные расходы).</li>
          <li><strong>Финансовая гибкость:</strong> Возможность не работать какое-то время или работать с меньшей нагрузкой.</li>
          <li><strong>Финансовая независимость:</strong> Пассивный доход полностью покрывает привычный образ жизни.</li>
          <li><strong>Финансовая свобода:</strong> Способность жить на пассивный доход, превышающий текущие потребности, с возможностью увеличивать расходы.</li>
        </ol>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Особенности достижения финансовой независимости в России</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            В российских реалиях при расчете учитывайте более высокую инфляцию, чем в развитых странах, возможную волатильность рубля, особенности налогообложения и доступные инструменты инвестирования (ИИС, НПФ, ПИФы, ETF и др.).
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-purple-500" />
          Инвестиционные стратегии для финансовой независимости
        </h3>
        <p>
          Для достижения финансовой независимости важно не только накапливать, но и грамотно инвестировать сбережения:
        </p>
        <ul>
          <li>
            <strong>Консервативная стратегия:</strong> Акцент на защиту капитала, низкий риск. Включает надежные облигации (ОФЗ, корпоративные), банковские депозиты, золото. Ожидаемая доходность в России — 5-8% годовых.
          </li>
          <li>
            <strong>Сбалансированная стратегия:</strong> Оптимальное соотношение риска и доходности. Распределение между акциями (40-60%) и облигациями (40-60%), небольшая доля альтернативных инвестиций. Ожидаемая доходность — 9-14% годовых.
          </li>
          <li>
            <strong>Агрессивная стратегия:</strong> Максимизация доходности при высоком риске. Преобладание акций (70-90%), включая высокорисковые инструменты. Подходит молодым инвесторам или для небольшой части портфеля. Потенциальная доходность — 15-20%+ годовых.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-500" />
          Инструменты для достижения финансовой независимости в России
        </h3>
        <ul>
          <li>
            <strong>Индивидуальный инвестиционный счет (ИИС):</strong> Специальный брокерский счет с налоговыми льготами (вычет до 52 000 ₽ в год или освобождение от НДФЛ на доход).
          </li>
          <li>
            <strong>Брокерский счет:</strong> Позволяет инвестировать в российские и зарубежные акции, облигации, ETF и другие инструменты. Основной счет для формирования долгосрочного инвестиционного портфеля.
          </li>
          <li>
            <strong>Негосударственные пенсионные фонды (НПФ):</strong> Добровольные пенсионные программы с налоговыми льготами, позволяющие формировать дополнительный пенсионный капитал.
          </li>
          <li>
            <strong>ПИФы и ETF:</strong> Позволяют инвестировать в диверсифицированные портфели акций, облигаций и других активов с низким порогом входа.
          </li>
          <li>
            <strong>Недвижимость:</strong> Приобретение недвижимости для получения арендного дохода или долгосрочного роста стоимости.
          </li>
          <li>
            <strong>Банковские депозиты:</strong> Для хранения финансовой подушки и консервативной части инвестиционного портфеля.
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Факторы, влияющие на достижение финансовой независимости
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Ваша норма сбережений (процент дохода, который удается откладывать)</li>
            <li>Доходность инвестиций с учетом инфляции (реальная доходность)</li>
            <li>Размер уже накопленного капитала</li>
            <li>Уровень желаемого пассивного дохода</li>
            <li>Налоговые особенности в стране проживания</li>
            <li>Доступные инвестиционные инструменты и их ликвидность</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-orange-500" />
          Стратегии ускорения пути к финансовой независимости
        </h3>
        <ol>
          <li><strong>Увеличение дохода:</strong> Развитие карьеры, поиск высокооплачиваемой работы, дополнительные источники заработка, фриланс, развитие бизнеса.</li>
          <li><strong>Оптимизация расходов:</strong> Разумное сокращение трат без снижения качества жизни, приоритизация расходов, планирование бюджета.</li>
          <li><strong>Увеличение нормы сбережений:</strong> Постепенное увеличение процента сбережений от дохода, например, на 1% каждые несколько месяцев.</li>
          <li><strong>Повышение финансовой грамотности:</strong> Изучение основ инвестирования, финансового планирования, налоговой оптимизации.</li>
          <li><strong>Создание дополнительных источников пассивного дохода:</strong> Инвестиции в бизнес, недвижимость, создание цифровых продуктов, авторские права.</li>
        </ol>

        <h3 className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-500" />
          Типичные ошибки на пути к финансовой независимости
        </h3>
        <ul>
          <li><strong>Откладывание начала:</strong> "Начну откладывать, когда буду зарабатывать больше". Чем раньше вы начнете, тем большую пользу принесет сложный процент.</li>
          <li><strong>Недооценка инфляции:</strong> Важно учитывать реальную (с поправкой на инфляцию) доходность инвестиций.</li>
          <li><strong>Избегание инвестиций из-за страха:</strong> Держать все сбережения на депозитах или в наличных — верный путь к потере покупательной способности из-за инфляции.</li>
          <li><strong>Попытки "обыграть рынок":</strong> Активная торговля и погоня за высокой доходностью часто приводят к худшим результатам, чем долгосрочное инвестирование.</li>
          <li><strong>Отсутствие диверсификации:</strong> "Все яйца в одной корзине" — опасная стратегия, особенно в российских реалиях.</li>
          <li><strong>Пренебрежение защитой капитала:</strong> Отсутствие страховки, завещания, надежного хранения данных может свести на нет годы накоплений.</li>
        </ul>

        <h3>Налоговые аспекты финансовой независимости в России</h3>
        <p>
          Эффективное налоговое планирование — важный элемент достижения финансовой независимости в России:
        </p>
        <ul>
          <li><strong>Налоговые вычеты:</strong> Инвестиционный вычет по ИИС (тип А), социальные вычеты (НПФ, страхование жизни), имущественные вычеты при покупке недвижимости.</li>
          <li><strong>Льготы для долгосрочных инвестиций:</strong> Освобождение от НДФЛ для ценных бумаг, находящихся в собственности более 3 лет (с ограничениями).</li>
          <li><strong>Оптимизация структуры активов:</strong> Размещение активов на счетах с наиболее выгодным налоговым режимом.</li>
          <li><strong>Налоговые льготы для ИП:</strong> Для тех, кто ведет бизнес — выбор оптимальной системы налогообложения (УСН, патент, самозанятость).</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор финансовой независимости поможет вам спланировать свой путь, оценить необходимый капитал, срок достижения цели и выбрать оптимальные параметры сбережений и инвестиций!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;